import React from 'react';
import { getSeoRouteByPath } from '../lib/seo-routes';

interface SEOProps {
  title: string;
  description: string;
  type?: 'website' | 'article';
  image?: string;
  author?: string;
  publishedDate?: string;
  lastUpdated?: string;
  canonical?: string;
}

/** Compatibility for page components; RouteMetadata owns the document head. */
export function SEO(_props: SEOProps) { return null; }
export function Schema(_props: { data: Record<string, unknown> }) { return null; }
export function WebsiteSchema() { return null; }
export function OrganizationSchema() { return null; }

/** Shared by client navigation and regression tests; never touches consent/ad tags. */
export function updateRouteHead(document: Document, path: string) {
  const route = getSeoRouteByPath(path);
  const title = route?.title || 'Page Not Found | airports.ng';
  const description = route?.description || 'This address does not match a current airports.ng page. Browse airport guides, travel tools, or the travel blog.';
  document.title = title;
  const updateMeta = (name: string, content: string, property = false) => {
    const attribute = property ? 'property' : 'name';
    let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attribute, name);
      document.head.appendChild(element);
    }
    element.content = content;
  };
  updateMeta('title', title);
  updateMeta('description', description);
  updateMeta('robots', route ? 'index, follow' : 'noindex, follow');
  for (const element of document.head.querySelectorAll('meta[property^="article:"], meta[property^="og:"], meta[name^="twitter:"]')) element.remove();
  const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!route) canonical?.remove();
  else {
    const link = canonical || document.createElement('link');
    link.rel = 'canonical';
    link.href = route.canonical;
    if (!canonical) document.head.appendChild(link);
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:type', route.kind === 'article' ? 'article' : 'website', true);
    updateMeta('og:url', route.canonical, true);
    updateMeta('og:image', route.image, true);
    updateMeta('og:site_name', 'airports.ng', true);
    updateMeta('og:locale', 'en_NG', true);
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:url', route.canonical);
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', route.image);
    const article = route.schema.find(schema => schema['@type'] === 'Article');
    if (article) {
      updateMeta('article:author', 'airports.ng Editorial Team', true);
      updateMeta('article:published_time', String(article.datePublished), true);
      updateMeta('article:modified_time', String(article.dateModified), true);
    }
  }
  for (const script of document.head.querySelectorAll('script[data-prerender-schema], script[data-schema-component], script[data-route-schema]')) script.remove();
  const siteSchemas = getSeoRouteByPath('/')?.schema || [];
  const schemas = route ? (route.path === '/' ? route.schema : [...siteSchemas, ...route.schema]) : [];
  for (const schema of schemas) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.routeSchema = 'true';
    script.textContent = JSON.stringify(schema).replace(/</g, '\\u003c');
    document.head.appendChild(script);
  }
}

export function RouteMetadata({ path }: { path: string }) {
  React.useEffect(() => { updateRouteHead(document, path); }, [path]);
  return null;
}
